import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { pick3 } from "../i18n/v3";
import { PageShell } from "../components/PageShell";
import PageBackdrop from "../components/PageBackdrop";
import useJsonLd from "../hooks/useJsonLd";
import useSeo from "../hooks/useSeo";
import useContentHreflang from "../hooks/useContentHreflang";
import useContentRedirect from "../hooks/useContentRedirect";
import ContentLangSwitcher from "../components/ContentLangSwitcher";
import { getProject } from "../data/projects";
import { API, pickContent, splitParagraphs, fileUrl } from "../lib/content";

const SITE = "https://lifeisaproject.global";
const ease = [0.16, 1, 0.3, 1];

/**
 * ProjectDetailPage — /projects/:slug.
 *
 * The CMS (database) is the source of truth: published projects created in
 * /admin/content render here with the sectioned editorial layout. The
 * legacy static registry (src/data/projects.js) is kept as a fallback so
 * existing routes never break. Drafts return 404 → redirect to /projects.
 *
 * CmsProjectView is the pure renderer — also used by the token-protected
 * draft preview (/preview/:token), so a draft looks exactly like the
 * final published page.
 */

const PROJECT_SECTIONS = [
    ["problem",         { en: "Problem & context", es: "Problema y contexto",   nl: "Probleem & context",  fr: "Problème & contexte" }],
    ["objectives",      { en: "Objectives",        es: "Objetivos",             nl: "Doelstellingen",      fr: "Objectifs" }],
    ["approach",        { en: "Approach",          es: "Enfoque",               nl: "Aanpak",              fr: "Approche" }],
    ["implementation",  { en: "Implementation",    es: "Implementación",        nl: "Uitvoering",          fr: "Mise en œuvre" }],
    ["results",         { en: "Results",           es: "Resultados",            nl: "Resultaten",          fr: "Résultats" }],
    ["impact",          { en: "Impact",            es: "Impacto",               nl: "Impact",              fr: "Impact" }],
    ["lessons",         { en: "Lessons learned",   es: "Lecciones aprendidas",  nl: "Geleerde lessen",     fr: "Leçons apprises" }],
];

const DOCS_LABEL = { en: "Supporting documents", es: "Documentos de apoyo", nl: "Ondersteunende documenten", fr: "Documents complémentaires" };
const LINKS_LABEL = { en: "Related links", es: "Enlaces relacionados", nl: "Gerelateerde links", fr: "Liens associés" };

function backLabel(lang) {
    return lang === "es" ? "← Proyectos" : lang === "nl" ? "← Projecten" : lang === "fr" ? "← Projets" : "← Projects";
}

/* ------------------------- CMS-backed project ------------------------- */

export function CmsProjectView({ item, previewToken = null }) {
    const { lang } = useLanguage();

    const { title, subtitle } = pickContent(item, lang);
    const meta = item
        ? [item.project_status, item.period, item.location, (item.language || "en").toUpperCase()].filter(Boolean)
        : [];
    const sections = item ? PROJECT_SECTIONS.filter(([key]) => (item[key] || "").trim()) : [];

    useSeo(item ? {
        title: item.seo_title || `${title} — Life is a Project`,
        description: item.seo_description || subtitle || undefined,
        path: `/projects/${item.slug}`,
        image: item.cover_image ? fileUrl(item.cover_image) : undefined,
    } : null);

    useContentHreflang(item, "projects");

    useJsonLd(item ? `article-project-${item.slug}` : null, item ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${SITE}/projects/${item.slug}`,
        headline: item.title,
        description: item.seo_description || item.subtitle || undefined,
        articleSection: "Projects",
        inLanguage: item.language || "en",
        mainEntityOfPage: `${SITE}/projects/${item.slug}`,
        url: `${SITE}/projects/${item.slug}`,
        isPartOf: { "@id": `${SITE}/#website` },
        publisher: { "@id": `${SITE}/#organization` },
        author: { "@type": "Organization", name: "Life is a Project", url: SITE },
    } : null);

    useJsonLd(item ? `breadcrumb-project-${item.slug}` : null, item ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",     item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Projects", item: `${SITE}/projects` },
            { "@type": "ListItem", position: 3, name: item.title, item: `${SITE}/projects/${item.slug}` },
        ],
    } : null);

    return (
        <PageShell testId="page-project-detail" backdrop={<PageBackdrop variant="projects" soft />}>
            {!previewToken && (
                <Link to="/projects" data-testid="project-back" className="link-editorial font-mono text-[11px] tracking-[0.28em] uppercase inline-block mb-10">
                    {backLabel(lang)}
                </Link>
            )}

            <header className="max-w-[820px] mb-16">
                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span aria-hidden className="gold-thin" />
                    {meta.map((m, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span aria-hidden className="text-[color:var(--line-strong)]">·</span>}
                            <span>{m}</span>
                        </React.Fragment>
                    ))}
                </div>
                <ContentLangSwitcher item={item} basePath="projects" />
                <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease }}
                    className="font-display font-light tracking-[-0.01em] leading-[1.05] text-[color:var(--navy)]"
                    style={{ fontSize: "clamp(36px, 5.4vw, 72px)" }}
                >
                    {title}
                </motion.h1>
                {subtitle && (
                    <p className="mt-8 font-display italic text-[22px] md:text-[26px] leading-[1.3] text-[color:var(--navy)]/80 max-w-[52ch]">
                        {subtitle}
                    </p>
                )}
                {(item.author || item.publication_date) && (
                    <p className="mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        {[item.author, item.publication_date].filter(Boolean).join(" · ")}
                    </p>
                )}
            </header>

            {item.cover_image && (
                <img
                    src={fileUrl(item.cover_image, previewToken)}
                    alt={title}
                    data-testid="content-cover-image"
                    className="w-full h-auto mb-16 border border-[color:var(--line)]"
                    loading="lazy"
                />
            )}

            {sections.length > 0 && (
                <article className="border-t border-[color:var(--line-strong)]">
                    {sections.map(([key, labels], idx) => (
                        <motion.section
                            key={key}
                            data-testid={`project-section-${key}`}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8%" }}
                            transition={{ duration: 0.75, ease, delay: (idx % 4) * 0.05 }}
                            className="grid grid-cols-12 gap-6 py-10 md:py-14 border-b border-[color:var(--line)]"
                        >
                            <div className="col-span-12 md:col-span-3">
                                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                                    {String(idx + 1).padStart(2, "0")}
                                </div>
                                <h2 className="mt-3 font-display text-[26px] md:text-[32px] leading-[1.1] text-[color:var(--navy)]">
                                    {labels[lang] || labels.en}
                                </h2>
                            </div>
                            <div className="col-span-12 md:col-span-9 space-y-5 max-w-[62ch]">
                                {splitParagraphs(item[key]).map((p, i) => (
                                    <p key={i} className="text-[16px] md:text-[18px] leading-[1.72] text-[color:var(--navy)]/90">{p}</p>
                                ))}
                            </div>
                        </motion.section>
                    ))}
                </article>
            )}

            {(item.gallery_images || []).length > 0 && (
                <div data-testid="project-gallery" className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {item.gallery_images.map((p, i) => (
                        <img key={p} src={fileUrl(p, previewToken)} alt={`${title} — ${i + 1}`} className="w-full h-auto border border-[color:var(--line)]" loading="lazy" />
                    ))}
                </div>
            )}

            {(item.documents || []).length > 0 && (
                <div className="mt-16 pt-8 border-t border-[color:var(--line)]">
                    <h2 className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-5">
                        {DOCS_LABEL[lang] || DOCS_LABEL.en}
                    </h2>
                    <ul className="space-y-3">
                        {item.documents.map((d) => (
                            <li key={d.path}>
                                <a href={fileUrl(d.path, previewToken)} target="_blank" rel="noopener noreferrer" data-testid="project-document-link" className="link-editorial font-display italic text-[17px] text-[color:var(--navy)]/85">
                                    {d.name} ↗
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {(item.external_links || []).length > 0 && (
                <div className="mt-10">
                    <h2 className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-5">
                        {LINKS_LABEL[lang] || LINKS_LABEL.en}
                    </h2>
                    <ul className="space-y-3">
                        {item.external_links.map((l, i) => (
                            <li key={i}>
                                <a href={l.url} target="_blank" rel="noopener noreferrer" className="link-editorial font-display italic text-[17px] text-[color:var(--navy)]/85">
                                    {l.label} ↗
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {item.project_status && (
                <div className="mt-24 pt-8 border-t border-[color:var(--line)] font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                    Status · {item.project_status}
                </div>
            )}
        </PageShell>
    );
}

function CmsProject({ slug }) {
    const [item, setItem] = useState(null);
    const [status, setStatus] = useState("loading");
    const redirectPending = useContentRedirect(status === "missing");

    useEffect(() => {
        setStatus("loading");
        setItem(null);
        axios
            .get(`${API}/content/projects/${slug}`)
            .then((r) => { setItem(r.data); setStatus("ready"); })
            .catch(() => setStatus("missing"));
    }, [slug]);

    if (status === "loading" || (status === "missing" && redirectPending)) {
        return (
            <PageShell testId="page-project-detail">
                <div className="py-16 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">Loading…</div>
            </PageShell>
        );
    }
    if (status === "missing" || !item) return <Navigate to="/projects" replace />;
    return <CmsProjectView item={item} />;
}

/* -------------------- Legacy static-registry project ------------------- */

function RegistryProject({ project }) {
    const { lang } = useLanguage();
    const title   = pick3(project.title, lang);
    const summary = pick3(project.summary, lang);

    useJsonLd(`article-project-${project.slug}`, {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${SITE}/projects/${project.slug}`,
        headline: title,
        description: summary || undefined,
        articleSection: "Projects",
        inLanguage: lang,
        mainEntityOfPage: `${SITE}/projects/${project.slug}`,
        url: `${SITE}/projects/${project.slug}`,
        isPartOf: { "@id": `${SITE}/#website` },
        publisher: { "@id": `${SITE}/#organization` },
        author: { "@type": "Organization", name: "Life is a Project", url: SITE },
    });

    useJsonLd(`breadcrumb-project-${project.slug}`, {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",     item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Projects", item: `${SITE}/projects` },
            { "@type": "ListItem", position: 3, name: title,      item: `${SITE}/projects/${project.slug}` },
        ],
    });

    const statusLabel = lang === "es" ? "Estado" : lang === "nl" ? "Status" : lang === "fr" ? "État" : "Status";

    const meta = [
        project.number,
        project.status,
        project.year,
        project.location,
    ].filter(Boolean);

    return (
        <PageShell testId="page-project-detail">
            <Link to="/projects" data-testid="project-back" className="link-editorial font-mono text-[11px] tracking-[0.28em] uppercase inline-block mb-10">
                {backLabel(lang)}
            </Link>

            <header className="max-w-[820px] mb-16">
                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span aria-hidden className="gold-thin" />
                    {meta.map((m, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span aria-hidden className="text-[color:var(--line-strong)]">·</span>}
                            <span>{m}</span>
                        </React.Fragment>
                    ))}
                </div>
                <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease }}
                    className="font-display font-light tracking-[-0.01em] leading-[1.05] text-[color:var(--navy)]"
                    style={{ fontSize: "clamp(36px, 5.4vw, 72px)" }}
                >
                    {title}
                </motion.h1>
                {summary && (
                    <p className="mt-8 font-display italic text-[22px] md:text-[26px] leading-[1.3] text-[color:var(--navy)]/80 max-w-[52ch]">
                        {summary}
                    </p>
                )}
            </header>

            <article className="border-t border-[color:var(--line-strong)]">
                {project.sections?.map((s, idx) => (
                    <motion.section
                        key={s.key}
                        aria-labelledby={`project-section-${s.key}`}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-8%" }}
                        transition={{ duration: 0.75, ease, delay: (idx % 4) * 0.05 }}
                        className="grid grid-cols-12 gap-6 py-10 md:py-14 border-b border-[color:var(--line)]"
                    >
                        <div className="col-span-12 md:col-span-3">
                            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                                {String(idx + 1).padStart(2, "0")} · {s.key}
                            </div>
                            <h2
                                id={`project-section-${s.key}`}
                                className="mt-3 font-display text-[26px] md:text-[32px] leading-[1.1] text-[color:var(--navy)]"
                            >
                                {pick3(s.heading, lang)}
                            </h2>
                        </div>
                        <div className="col-span-12 md:col-span-9 space-y-5 max-w-[62ch]">
                            {s.body?.map((p, i) => (
                                <p key={i} className="text-[16px] md:text-[18px] leading-[1.72] text-[color:var(--navy)]/90">
                                    {pick3(p, lang)}
                                </p>
                            ))}
                        </div>
                    </motion.section>
                ))}
            </article>

            {project.pullQuote && (
                <blockquote className="mt-20 max-w-[46ch]">
                    <p className="font-display italic text-[26px] md:text-[38px] leading-[1.18] tracking-[-0.005em] text-[color:var(--navy)]">
                        “{pick3(project.pullQuote.text, lang)}”
                    </p>
                    {project.pullQuote.attribution && (
                        <footer className="mt-8 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                            {pick3(project.pullQuote.attribution, lang)}
                        </footer>
                    )}
                </blockquote>
            )}

            <div className="mt-24 pt-8 border-t border-[color:var(--line)] font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                {statusLabel} · {project.status}
            </div>
        </PageShell>
    );
}

export default function ProjectDetailPage() {
    const { slug } = useParams();
    const registryProject = getProject(slug);
    if (registryProject) return <RegistryProject project={registryProject} />;
    return <CmsProject slug={slug} />;
}
